/**
 * FIKO HIERARCHICAL Z-BUFFER OCCLUSION CULLING
 * ============================================
 * FAZA 4.1: CATIA seviyesi occlusion sistemi
 *
 * 100M+ triangle sahneleri icin ultra-performansli occlusion culling
 *
 * Ozellikler:
 * - HiZ mip chain: GPU'da hierarchical depth buffer
 * - Conservative depth testing: False negative'leri onle
 * - WebGPU compute shader pipeline
 * - WebGL2 fallback (async PBO readback)
 * - Batch occlusion queries (binlerce obje tek seferde)
 * - Temporal coherence: Frame-to-frame visibility cache
 * - Adaptive update frequency: Scene complexity'e gore
 */

import * as THREE from 'three';

// ============================================================================
// INTERFACES & TYPES
// ============================================================================

export interface HiZConfig {
    /** HiZ pyramid resolution (power of 2) */
    pyramidSize: number;
    /** Number of mip levels */
    mipLevels: number;
    /** Depth bias for conservative testing */
    depthBias: number;
    /** Update frequency (frames) */
    updateFrequency: number;
    /** Enable temporal coherence caching */
    useTemporalCoherence: boolean;
    /** Batch size for GPU queries */
    batchSize: number;
    /** Use WebGPU if available */
    preferWebGPU: boolean;
}

export interface OcclusionQuery {
    objectId: string;
    screenBounds: { minX: number; minY: number; maxX: number; maxY: number };
    objectDepth: number;
    mipLevel: number;
}

export interface OcclusionResult {
    objectId: string;
    isVisible: boolean;
    sampledDepth: number;
    confidence: number;
}

export interface HiZStats {
    totalQueries: number;
    gpuQueries: number;
    cpuQueries: number;
    objectsCulled: number;
    visibleObjects: number;
    avgQueryTimeMs: number;
    mipChainBuildTimeMs: number;
    isWebGPU: boolean;
    pyramidSize: number;
    activeMipLevels: number;
}

// ============================================================================
// WGSL SHADERS
// ============================================================================

/** HiZ Mip Chain Builder - GPU'da depth pyramid olustur */
const HIZ_MIP_BUILD_WGSL = `
struct MipParams {
    inputSize: vec2u,
    outputSize: vec2u,
    mipLevel: u32,
    useMax: u32,  // 1 = max (conservative), 0 = min
    _pad0: u32,
    _pad1: u32
}

@group(0) @binding(0) var inputMip: texture_2d<f32>;
@group(0) @binding(1) var outputMip: texture_storage_2d<r32float, write>;
@group(0) @binding(2) var<uniform> params: MipParams;

@compute @workgroup_size(16, 16)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    if (gid.x >= params.outputSize.x || gid.y >= params.outputSize.y) {
        return;
    }

    // Sample 2x2 texels from previous mip level
    let baseCoord = vec2i(gid.xy) * 2;

    // Clamp to valid range
    let maxCoord = vec2i(params.inputSize) - 1;
    let c00 = min(baseCoord, maxCoord);
    let c10 = min(baseCoord + vec2i(1, 0), maxCoord);
    let c01 = min(baseCoord + vec2i(0, 1), maxCoord);
    let c11 = min(baseCoord + vec2i(1, 1), maxCoord);

    let d00 = textureLoad(inputMip, c00, 0).r;
    let d10 = textureLoad(inputMip, c10, 0).r;
    let d01 = textureLoad(inputMip, c01, 0).r;
    let d11 = textureLoad(inputMip, c11, 0).r;

    // Conservative: use MAX depth (furthest) for occlusion testing
    // This ensures we don't incorrectly cull visible objects
    var result: f32;
    if (params.useMax == 1u) {
        result = max(max(d00, d10), max(d01, d11));
    } else {
        result = min(min(d00, d10), min(d01, d11));
    }

    textureStore(outputMip, vec2i(gid.xy), vec4f(result, 0.0, 0.0, 1.0));
}
`;

/** Batch Occlusion Query - Binlerce objeyi tek seferde test et */
const BATCH_OCCLUSION_QUERY_WGSL = `
struct QueryInput {
    screenMinX: f32,
    screenMinY: f32,
    screenMaxX: f32,
    screenMaxY: f32,
    objectDepth: f32,
    objectIndex: u32,
    _pad0: f32,
    _pad1: f32
}

struct QueryResult {
    isVisible: u32,
    sampledDepth: f32,
    objectIndex: u32,
    confidence: f32
}

struct QueryParams {
    pyramidSize: vec2f,
    depthBias: f32,
    maxMipLevel: u32,
    queryCount: u32,
    _pad0: u32,
    _pad1: u32,
    _pad2: u32
}

@group(0) @binding(0) var hizPyramid: texture_2d<f32>;
@group(0) @binding(1) var<storage, read> queries: array<QueryInput>;
@group(0) @binding(2) var<storage, read_write> results: array<QueryResult>;
@group(0) @binding(3) var<uniform> params: QueryParams;

// Calculate appropriate mip level based on screen-space size
fn calculateMipLevel(screenSize: vec2f) -> u32 {
    let pixelSize = screenSize * params.pyramidSize;
    let maxPixelDim = max(pixelSize.x, pixelSize.y);

    // Use mip level where 1 texel covers the object's screen bounds
    // Add 1 for conservative testing (sample slightly larger area)
    let mip = u32(ceil(log2(max(1.0, maxPixelDim)))) + 1u;

    return min(mip, params.maxMipLevel);
}

@compute @workgroup_size(256)
fn main(@builtin(global_invocation_id) gid: vec3u) {
    let idx = gid.x;
    if (idx >= params.queryCount) {
        return;
    }

    let query = queries[idx];

    // Calculate screen-space size
    let screenSize = vec2f(
        query.screenMaxX - query.screenMinX,
        query.screenMaxY - query.screenMinY
    );

    // Determine mip level
    let mipLevel = calculateMipLevel(screenSize);

    // Calculate sample position (center of bounds)
    let center = vec2f(
        (query.screenMinX + query.screenMaxX) * 0.5,
        (query.screenMinY + query.screenMaxY) * 0.5
    );

    // Convert to texel coordinates at this mip level
    let mipSize = params.pyramidSize / f32(1u << mipLevel);
    let texCoord = vec2i(center * mipSize);
    let clampedCoord = clamp(texCoord, vec2i(0), vec2i(mipSize) - 1);

    // Sample HiZ pyramid at calculated mip level
    let sampledDepth = textureLoad(hizPyramid, clampedCoord, i32(mipLevel)).r;

    // Occlusion test with depth bias
    // Object is visible if its depth is less than or equal to sampled depth
    var result: QueryResult;
    result.objectIndex = query.objectIndex;
    result.sampledDepth = sampledDepth;

    // Calculate confidence based on mip level (higher mip = lower confidence)
    result.confidence = 1.0 - (f32(mipLevel) / f32(params.maxMipLevel));

    if (query.objectDepth <= sampledDepth + params.depthBias) {
        result.isVisible = 1u;
    } else {
        result.isVisible = 0u;
    }

    results[idx] = result;
}
`;

/** Single Sample Query - Tek obje icin hizli test */
const SINGLE_QUERY_WGSL = `
struct SingleQuery {
    centerX: f32,
    centerY: f32,
    objectDepth: f32,
    mipLevel: u32
}

struct SingleResult {
    isVisible: u32,
    sampledDepth: f32,
    _pad0: u32,
    _pad1: u32
}

@group(0) @binding(0) var hizPyramid: texture_2d<f32>;
@group(0) @binding(1) var<uniform> query: SingleQuery;
@group(0) @binding(2) var<storage, read_write> result: SingleResult;
@group(0) @binding(3) var<uniform> depthBias: f32;

@compute @workgroup_size(1)
fn main() {
    let texSize = textureDimensions(hizPyramid, i32(query.mipLevel));
    let texCoord = vec2i(
        i32(query.centerX * f32(texSize.x)),
        i32(query.centerY * f32(texSize.y))
    );
    let clampedCoord = clamp(texCoord, vec2i(0), vec2i(texSize) - 1);

    let sampledDepth = textureLoad(hizPyramid, clampedCoord, i32(query.mipLevel)).r;

    result.sampledDepth = sampledDepth;

    if (query.objectDepth <= sampledDepth + depthBias) {
        result.isVisible = 1u;
    } else {
        result.isVisible = 0u;
    }
}
`;

// ============================================================================
// GPU BUFFER USAGE FLAGS
// ============================================================================

const GPU_BUFFER_USAGE = {
    MAP_READ: 0x0001,
    MAP_WRITE: 0x0002,
    COPY_SRC: 0x0004,
    COPY_DST: 0x0008,
    INDEX: 0x0010,
    VERTEX: 0x0020,
    UNIFORM: 0x0040,
    STORAGE: 0x0080,
    INDIRECT: 0x0100,
    QUERY_RESOLVE: 0x0200
};

// ============================================================================
// MAIN CLASS
// ============================================================================

export class FikoHiZOcclusionCulling {
    private config: HiZConfig;
    private camera: THREE.Camera;
    private renderer: THREE.WebGLRenderer;

    // GPU State
    private gpuDevice: GPUDevice | null = null;
    private isWebGPU: boolean = false;
    private initPromise: Promise<boolean> | null = null;

    // WebGPU Pipelines
    private mipBuildPipeline: GPUComputePipeline | null = null;
    private batchQueryPipeline: GPUComputePipeline | null = null;
    private mipBuildBindGroupLayout: any = null; // GPUBindGroupLayout
    private batchQueryBindGroupLayout: any = null; // GPUBindGroupLayout

    // HiZ Pyramid Textures (WebGPU types - using any for compatibility)
    private hizPyramid: any = null; // GPUTexture
    private hizPyramidViews: any[] = []; // GPUTextureView[]
    private hizStorageViews: any[] = []; // GPUTextureView[]

    // WebGL2 Fallback
    private depthRenderTarget: THREE.WebGLRenderTarget | null = null;
    private depthMaterial: THREE.ShaderMaterial | null = null;
    private hizPyramidCanvas: HTMLCanvasElement | null = null;
    private hizPyramidLevels: Float32Array[] = [];

    // Object Management
    private occluders: Set<THREE.Mesh> = new Set();
    private occludees: Map<string, THREE.Object3D> = new Map();
    private boundingSpheres: Map<string, { center: THREE.Vector3; radius: number }> = new Map();

    // Visibility Cache (Temporal Coherence)
    private visibilityCache: Map<string, { visible: boolean; frame: number; confidence: number }> = new Map();
    private frameCounter: number = 0;

    // Stats
    private stats: HiZStats = {
        totalQueries: 0,
        gpuQueries: 0,
        cpuQueries: 0,
        objectsCulled: 0,
        visibleObjects: 0,
        avgQueryTimeMs: 0,
        mipChainBuildTimeMs: 0,
        isWebGPU: false,
        pyramidSize: 0,
        activeMipLevels: 0
    };

    // Pre-allocated buffers
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempBox3 = new THREE.Box3();
    private static readonly _tempMatrix4 = new THREE.Matrix4();
    private static readonly _tempFrustum = new THREE.Frustum();

    constructor(
        camera: THREE.Camera,
        renderer: THREE.WebGLRenderer,
        config?: Partial<HiZConfig>
    ) {
        this.camera = camera;
        this.renderer = renderer;

        this.config = {
            pyramidSize: config?.pyramidSize ?? 512,
            mipLevels: config?.mipLevels ?? 9, // 512 -> 1
            depthBias: config?.depthBias ?? 0.001,
            updateFrequency: config?.updateFrequency ?? 2,
            useTemporalCoherence: config?.useTemporalCoherence ?? true,
            batchSize: config?.batchSize ?? 4096,
            preferWebGPU: config?.preferWebGPU ?? true
        };

        this.stats.pyramidSize = this.config.pyramidSize;
        this.stats.activeMipLevels = this.config.mipLevels;

        // Initialize WebGL2 fallback immediately
        this.initWebGL2Fallback();

        // Try WebGPU (non-blocking)
        if (this.config.preferWebGPU) {
            this.initWebGPU();
        }
    }

    // ========================================================================
    // INITIALIZATION
    // ========================================================================

    /**
     * Initialize WebGPU for HiZ operations
     */
    async initWebGPU(): Promise<boolean> {
        if (this.initPromise) {
            return this.initPromise;
        }

        this.initPromise = this._initWebGPUInternal();
        return this.initPromise;
    }

    private async _initWebGPUInternal(): Promise<boolean> {
        try {
            if (typeof navigator === 'undefined' || !navigator.gpu) {
                console.log('[FikoHiZ] WebGPU not available, using WebGL2 fallback');
                return false;
            }

            const adapter = await navigator.gpu.requestAdapter({
                powerPreference: 'high-performance'
            });

            if (!adapter) {
                return false;
            }

            const device = await adapter.requestDevice({
                requiredFeatures: [],
                requiredLimits: {} as any
            });

            if (!device) {
                return false;
            }

            this.gpuDevice = device;

            // Handle device loss
            (device as any).lost?.then?.((info: any) => {
                console.warn('[FikoHiZ] WebGPU device lost:', info?.message);
                this.isWebGPU = false;
                this.gpuDevice = null;
            });

            // Create pipelines
            await this.createWebGPUPipelines();

            // Create HiZ pyramid texture
            this.createHiZPyramid();

            this.isWebGPU = true;
            this.stats.isWebGPU = true;
            console.log('[FikoHiZ] WebGPU initialized successfully');

            return true;

        } catch (e) {
            console.warn('[FikoHiZ] WebGPU init failed:', e);
            this.isWebGPU = false;
            return false;
        }
    }

    /**
     * Create WebGPU compute pipelines
     */
    private async createWebGPUPipelines(): Promise<void> {
        if (!this.gpuDevice) return;

        try {
            // Mip Build Pipeline
            const mipBuildModule = this.gpuDevice.createShaderModule({
                code: HIZ_MIP_BUILD_WGSL
            });

            const GPUShaderStageCompute = (window as any).GPUShaderStage?.COMPUTE ?? 0x4;
            this.mipBuildBindGroupLayout = (this.gpuDevice as any).createBindGroupLayout({
                entries: [
                    { binding: 0, visibility: GPUShaderStageCompute, texture: { sampleType: 'unfilterable-float' } },
                    { binding: 1, visibility: GPUShaderStageCompute, storageTexture: { access: 'write-only', format: 'r32float' } },
                    { binding: 2, visibility: GPUShaderStageCompute, buffer: { type: 'uniform' } }
                ]
            });

            this.mipBuildPipeline = this.gpuDevice.createComputePipeline({
                layout: this.gpuDevice.createPipelineLayout({
                    bindGroupLayouts: [this.mipBuildBindGroupLayout]
                }),
                compute: {
                    module: mipBuildModule,
                    entryPoint: 'main'
                }
            });

            // Batch Query Pipeline
            const batchQueryModule = this.gpuDevice.createShaderModule({
                code: BATCH_OCCLUSION_QUERY_WGSL
            });

            this.batchQueryBindGroupLayout = (this.gpuDevice as any).createBindGroupLayout({
                entries: [
                    { binding: 0, visibility: GPUShaderStageCompute, texture: { sampleType: 'unfilterable-float' } },
                    { binding: 1, visibility: GPUShaderStageCompute, buffer: { type: 'read-only-storage' } },
                    { binding: 2, visibility: GPUShaderStageCompute, buffer: { type: 'storage' } },
                    { binding: 3, visibility: GPUShaderStageCompute, buffer: { type: 'uniform' } }
                ]
            });

            this.batchQueryPipeline = this.gpuDevice.createComputePipeline({
                layout: this.gpuDevice.createPipelineLayout({
                    bindGroupLayouts: [this.batchQueryBindGroupLayout]
                }),
                compute: {
                    module: batchQueryModule,
                    entryPoint: 'main'
                }
            });

        } catch (e) {
            console.warn('[FikoHiZ] Pipeline creation failed:', e);
        }
    }

    /**
     * Create HiZ pyramid texture with mip levels
     */
    private createHiZPyramid(): void {
        if (!this.gpuDevice) return;

        const size = this.config.pyramidSize;

        const GPUTextureUsageFlags = (window as any).GPUTextureUsage ?? { TEXTURE_BINDING: 0x04, STORAGE_BINDING: 0x08, COPY_DST: 0x02 };
        this.hizPyramid = (this.gpuDevice as any).createTexture({
            size: { width: size, height: size, depthOrArrayLayers: 1 },
            format: 'r32float',
            usage: GPUTextureUsageFlags.TEXTURE_BINDING | GPUTextureUsageFlags.STORAGE_BINDING | GPUTextureUsageFlags.COPY_DST,
            mipLevelCount: this.config.mipLevels
        });

        // Create views for each mip level
        this.hizPyramidViews = [];
        this.hizStorageViews = [];

        for (let i = 0; i < this.config.mipLevels; i++) {
            // Read view
            this.hizPyramidViews.push(
                this.hizPyramid.createView({
                    baseMipLevel: i,
                    mipLevelCount: 1
                })
            );

            // Storage write view
            this.hizStorageViews.push(
                this.hizPyramid.createView({
                    baseMipLevel: i,
                    mipLevelCount: 1
                })
            );
        }
    }

    /**
     * Initialize WebGL2 fallback rendering
     */
    private initWebGL2Fallback(): void {
        const size = this.config.pyramidSize;

        // Depth render target
        this.depthRenderTarget = new THREE.WebGLRenderTarget(size, size, {
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RGBAFormat,
            type: THREE.FloatType,
            depthBuffer: true,
            stencilBuffer: false
        });

        // Depth material
        this.depthMaterial = new THREE.ShaderMaterial({
            vertexShader: `
                varying float vDepth;
                void main() {
                    vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                    gl_Position = projectionMatrix * mvPosition;
                    // Normalize depth to [0, 1] range
                    vDepth = gl_Position.z / gl_Position.w * 0.5 + 0.5;
                }
            `,
            fragmentShader: `
                varying float vDepth;
                void main() {
                    gl_FragColor = vec4(vDepth, vDepth, vDepth, 1.0);
                }
            `
        });

        // CPU-side HiZ pyramid
        this.hizPyramidLevels = [];
        let currentSize = size;
        for (let i = 0; i < this.config.mipLevels; i++) {
            this.hizPyramidLevels.push(new Float32Array(currentSize * currentSize));
            currentSize = Math.max(1, Math.floor(currentSize / 2));
        }
    }

    // ========================================================================
    // OCCLUDER/OCCLUDEE MANAGEMENT
    // ========================================================================

    /**
     * Register a mesh as an occluder (will block other objects)
     */
    addOccluder(mesh: THREE.Mesh, priority: number = 1): void {
        (mesh as any)._occluderPriority = priority;
        this.occluders.add(mesh);
    }

    /**
     * Remove an occluder
     */
    removeOccluder(mesh: THREE.Mesh): void {
        this.occluders.delete(mesh);
    }

    /**
     * Register an object for occlusion testing
     */
    addOccludee(id: string, object: THREE.Object3D): void {
        this.occludees.set(id, object);

        // Cache bounding sphere
        FikoHiZOcclusionCulling._tempBox3.setFromObject(object);
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        FikoHiZOcclusionCulling._tempBox3.getCenter(center);
        FikoHiZOcclusionCulling._tempBox3.getSize(size);

        this.boundingSpheres.set(id, {
            center: center.clone(),
            radius: size.length() * 0.5
        });
    }

    /**
     * Remove an occludee
     */
    removeOccludee(id: string): void {
        this.occludees.delete(id);
        this.boundingSpheres.delete(id);
        this.visibilityCache.delete(id);
    }

    /**
     * Update bounding sphere for an object (call after transform changes)
     */
    updateBoundingSphere(id: string): void {
        const object = this.occludees.get(id);
        if (!object) return;

        FikoHiZOcclusionCulling._tempBox3.setFromObject(object);
        const center = new THREE.Vector3();
        const size = new THREE.Vector3();
        FikoHiZOcclusionCulling._tempBox3.getCenter(center);
        FikoHiZOcclusionCulling._tempBox3.getSize(size);

        const sphere = this.boundingSpheres.get(id);
        if (sphere) {
            sphere.center.copy(center);
            sphere.radius = size.length() * 0.5;
        } else {
            this.boundingSpheres.set(id, { center, radius: size.length() * 0.5 });
        }
    }

    // ========================================================================
    // HiZ PYRAMID BUILDING
    // ========================================================================

    /**
     * Build HiZ pyramid from current depth buffer
     */
    async buildHiZPyramid(scene: THREE.Scene): Promise<void> {
        const startTime = performance.now();

        // First, render occluders to depth texture
        this.renderOccludersToDepth(scene);

        if (this.isWebGPU && this.gpuDevice) {
            await this.buildHiZPyramidWebGPU();
        } else {
            this.buildHiZPyramidCPU();
        }

        this.stats.mipChainBuildTimeMs = performance.now() - startTime;
    }

    /**
     * Render occluders to depth texture
     */
    private renderOccludersToDepth(scene: THREE.Scene): void {
        if (!this.depthRenderTarget || !this.depthMaterial) return;

        // Save state
        const originalVisibility = new Map<THREE.Object3D, boolean>();
        const originalMaterials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
        const originalRenderTarget = this.renderer.getRenderTarget();

        scene.traverse(obj => {
            originalVisibility.set(obj, obj.visible);
            if (obj instanceof THREE.Mesh) {
                originalMaterials.set(obj, obj.material);
            }
        });

        // Hide all, show only occluders
        scene.traverse(obj => { obj.visible = false; });

        this.occluders.forEach(occluder => {
            occluder.visible = true;
            occluder.material = this.depthMaterial!;
            // Ensure parents are visible
            let parent = occluder.parent;
            while (parent) {
                parent.visible = true;
                parent = parent.parent;
            }
        });

        // Render to depth target
        this.renderer.setRenderTarget(this.depthRenderTarget);
        this.renderer.clear();
        this.renderer.render(scene, this.camera);
        this.renderer.setRenderTarget(originalRenderTarget);

        // Restore state
        originalVisibility.forEach((visible, obj) => { obj.visible = visible; });
        originalMaterials.forEach((material, mesh) => { mesh.material = material; });
    }

    /**
     * Build HiZ pyramid using WebGPU compute
     */
    private async buildHiZPyramidWebGPU(): Promise<void> {
        if (!this.gpuDevice || !this.hizPyramid || !this.mipBuildPipeline) return;

        // TODO: Copy depth from WebGL to WebGPU texture
        // This requires WebGL-WebGPU interop which is not yet standardized
        // For now, we fall back to CPU mip building

        // When WebGL-WebGPU interop is available:
        // 1. Copy depth texture from WebGL to WebGPU
        // 2. Build mip chain using compute shader
        // 3. Use GPU for occlusion queries

        // Fallback to CPU for mip building
        this.buildHiZPyramidCPU();
    }

    /**
     * Build HiZ pyramid on CPU (WebGL2 fallback)
     */
    private buildHiZPyramidCPU(): void {
        if (!this.depthRenderTarget || this.hizPyramidLevels.length === 0) return;

        const gl = this.renderer.getContext() as WebGL2RenderingContext;
        if (!gl) return;

        const size = this.config.pyramidSize;

        // Read base depth from render target
        const props = this.renderer.properties.get(this.depthRenderTarget) as any;
        const framebuffer = props?.__webglFramebuffer;
        if (!framebuffer) return;

        gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);

        // Read into level 0
        const baseLevel = this.hizPyramidLevels[0];
        const tempPixels = new Float32Array(size * size * 4);
        gl.readPixels(0, 0, size, size, gl.RGBA, gl.FLOAT, tempPixels);

        // Extract R channel (depth)
        for (let i = 0; i < size * size; i++) {
            baseLevel[i] = tempPixels[i * 4];
        }

        gl.bindFramebuffer(gl.FRAMEBUFFER, null);

        // Build mip chain
        let currentSize = size;
        for (let level = 1; level < this.hizPyramidLevels.length; level++) {
            const prevLevel = this.hizPyramidLevels[level - 1];
            const currentLevel = this.hizPyramidLevels[level];
            const prevSize = currentSize;
            currentSize = Math.max(1, Math.floor(currentSize / 2));

            for (let y = 0; y < currentSize; y++) {
                for (let x = 0; x < currentSize; x++) {
                    const baseX = x * 2;
                    const baseY = y * 2;

                    // Sample 2x2 and take max (conservative)
                    const d00 = prevLevel[baseY * prevSize + baseX];
                    const d10 = prevLevel[baseY * prevSize + Math.min(baseX + 1, prevSize - 1)];
                    const d01 = prevLevel[Math.min(baseY + 1, prevSize - 1) * prevSize + baseX];
                    const d11 = prevLevel[Math.min(baseY + 1, prevSize - 1) * prevSize + Math.min(baseX + 1, prevSize - 1)];

                    currentLevel[y * currentSize + x] = Math.max(d00, d10, d01, d11);
                }
            }
        }
    }

    // ========================================================================
    // OCCLUSION QUERIES
    // ========================================================================

    /**
     * Perform batch occlusion query for all registered occludees
     */
    async queryAllOccludees(): Promise<Map<string, boolean>> {
        const startTime = performance.now();
        this.frameCounter++;

        const results = new Map<string, boolean>();

        // Build queries
        const queries: OcclusionQuery[] = [];

        this.occludees.forEach((object, id) => {
            // Check temporal cache first
            if (this.config.useTemporalCoherence) {
                const cached = this.visibilityCache.get(id);
                if (cached && (this.frameCounter - cached.frame) < this.config.updateFrequency && cached.confidence > 0.7) {
                    results.set(id, cached.visible);
                    return;
                }
            }

            const query = this.prepareQuery(id, object);
            if (query) {
                queries.push(query);
            } else {
                // Object is behind camera or outside frustum
                results.set(id, false);
            }
        });

        // Execute batch query
        if (queries.length > 0) {
            const queryResults = await this.executeBatchQuery(queries);

            for (const result of queryResults) {
                results.set(result.objectId, result.isVisible);

                // Update cache
                if (this.config.useTemporalCoherence) {
                    this.visibilityCache.set(result.objectId, {
                        visible: result.isVisible,
                        frame: this.frameCounter,
                        confidence: result.confidence
                    });
                }

                // Update stats
                if (result.isVisible) {
                    this.stats.visibleObjects++;
                } else {
                    this.stats.objectsCulled++;
                }
            }
        }

        this.stats.totalQueries += queries.length;
        this.stats.avgQueryTimeMs = (performance.now() - startTime) / Math.max(1, queries.length);

        return results;
    }

    /**
     * Prepare a single occlusion query
     */
    private prepareQuery(id: string, object: THREE.Object3D): OcclusionQuery | null {
        const sphere = this.boundingSpheres.get(id);
        if (!sphere) return null;

        // Project center to screen
        FikoHiZOcclusionCulling._tempVec3.copy(sphere.center);
        FikoHiZOcclusionCulling._tempVec3.project(this.camera);

        // Behind camera check
        if (FikoHiZOcclusionCulling._tempVec3.z < 0 || FikoHiZOcclusionCulling._tempVec3.z > 1) {
            return null;
        }

        // Calculate screen-space bounds from sphere
        const screenX = (FikoHiZOcclusionCulling._tempVec3.x + 1) * 0.5;
        const screenY = (1 - FikoHiZOcclusionCulling._tempVec3.y) * 0.5;

        // Approximate screen-space radius
        const dist = sphere.center.distanceTo(this.camera.position);
        const fov = (this.camera as THREE.PerspectiveCamera).fov * Math.PI / 180;
        const screenRadius = (sphere.radius / (2 * dist * Math.tan(fov / 2)));

        // Calculate mip level based on screen size
        const screenSize = screenRadius * 2;
        const pixelSize = screenSize * this.config.pyramidSize;
        const mipLevel = Math.min(
            this.config.mipLevels - 1,
            Math.max(0, Math.ceil(Math.log2(Math.max(1, pixelSize))))
        );

        return {
            objectId: id,
            screenBounds: {
                minX: Math.max(0, screenX - screenRadius),
                minY: Math.max(0, screenY - screenRadius),
                maxX: Math.min(1, screenX + screenRadius),
                maxY: Math.min(1, screenY + screenRadius)
            },
            objectDepth: FikoHiZOcclusionCulling._tempVec3.z,
            mipLevel
        };
    }

    /**
     * Execute batch occlusion query
     */
    private async executeBatchQuery(queries: OcclusionQuery[]): Promise<OcclusionResult[]> {
        if (this.isWebGPU && this.gpuDevice && this.batchQueryPipeline) {
            return this.executeBatchQueryWebGPU(queries);
        } else {
            return this.executeBatchQueryCPU(queries);
        }
    }

    /**
     * Execute batch query using WebGPU compute
     */
    private async executeBatchQueryWebGPU(queries: OcclusionQuery[]): Promise<OcclusionResult[]> {
        // TODO: Implement when WebGL-WebGPU interop is available
        // For now, fall back to CPU
        this.stats.gpuQueries += queries.length;
        return this.executeBatchQueryCPU(queries);
    }

    /**
     * Execute batch query on CPU
     */
    private executeBatchQueryCPU(queries: OcclusionQuery[]): OcclusionResult[] {
        const results: OcclusionResult[] = [];

        for (const query of queries) {
            const mipLevel = Math.min(query.mipLevel, this.hizPyramidLevels.length - 1);
            const level = this.hizPyramidLevels[mipLevel];

            if (!level) {
                results.push({
                    objectId: query.objectId,
                    isVisible: true,
                    sampledDepth: 1,
                    confidence: 0
                });
                continue;
            }

            const mipSize = Math.max(1, Math.floor(this.config.pyramidSize / Math.pow(2, mipLevel)));

            // Sample center
            const centerX = (query.screenBounds.minX + query.screenBounds.maxX) * 0.5;
            const centerY = (query.screenBounds.minY + query.screenBounds.maxY) * 0.5;

            const texX = Math.min(mipSize - 1, Math.max(0, Math.floor(centerX * mipSize)));
            const texY = Math.min(mipSize - 1, Math.max(0, Math.floor(centerY * mipSize)));

            const sampledDepth = level[texY * mipSize + texX];
            const isVisible = query.objectDepth <= sampledDepth + this.config.depthBias;

            results.push({
                objectId: query.objectId,
                isVisible,
                sampledDepth,
                confidence: 1 - (mipLevel / this.config.mipLevels)
            });
        }

        this.stats.cpuQueries += queries.length;
        return results;
    }

    /**
     * Quick single object visibility test
     */
    isVisible(id: string): boolean {
        // Check cache first
        if (this.config.useTemporalCoherence) {
            const cached = this.visibilityCache.get(id);
            if (cached && (this.frameCounter - cached.frame) < this.config.updateFrequency) {
                return cached.visible;
            }
        }

        const object = this.occludees.get(id);
        if (!object) return true;

        const query = this.prepareQuery(id, object);
        if (!query) return false;

        const results = this.executeBatchQueryCPU([query]);
        return results[0]?.isVisible ?? true;
    }

    // ========================================================================
    // FRUSTUM CULLING
    // ========================================================================

    /**
     * Combined frustum + occlusion culling
     */
    async cullObjects(objects: Map<string, THREE.Object3D>, scene: THREE.Scene): Promise<string[]> {
        // Build HiZ pyramid
        await this.buildHiZPyramid(scene);

        // Register all objects
        objects.forEach((obj, id) => {
            if (!this.occludees.has(id)) {
                this.addOccludee(id, obj);
            }
        });

        // Run frustum culling first
        const frustumVisible = this.frustumCull(objects);

        // Then occlusion culling on frustum-visible objects
        const filteredOccludees = new Map<string, THREE.Object3D>();
        frustumVisible.forEach(id => {
            const obj = objects.get(id);
            if (obj) filteredOccludees.set(id, obj);
        });

        // Update occludees map for query
        this.occludees = filteredOccludees;

        const visibility = await this.queryAllOccludees();

        const visibleIds: string[] = [];
        visibility.forEach((visible, id) => {
            if (visible) visibleIds.push(id);
        });

        return visibleIds;
    }

    /**
     * Frustum culling only
     */
    frustumCull(objects: Map<string, THREE.Object3D>): string[] {
        FikoHiZOcclusionCulling._tempMatrix4.multiplyMatrices(
            this.camera.projectionMatrix,
            this.camera.matrixWorldInverse
        );
        FikoHiZOcclusionCulling._tempFrustum.setFromProjectionMatrix(
            FikoHiZOcclusionCulling._tempMatrix4
        );

        const visible: string[] = [];

        objects.forEach((obj, id) => {
            const sphere = this.boundingSpheres.get(id);
            if (sphere) {
                const threeSphere = new THREE.Sphere(sphere.center, sphere.radius);
                if (FikoHiZOcclusionCulling._tempFrustum.intersectsSphere(threeSphere)) {
                    visible.push(id);
                }
            } else {
                FikoHiZOcclusionCulling._tempBox3.setFromObject(obj);
                if (FikoHiZOcclusionCulling._tempFrustum.intersectsBox(FikoHiZOcclusionCulling._tempBox3)) {
                    visible.push(id);
                }
            }
        });

        return visible;
    }

    // ========================================================================
    // STATS & CLEANUP
    // ========================================================================

    /**
     * Get performance statistics
     */
    getStats(): HiZStats {
        return { ...this.stats };
    }

    /**
     * Reset statistics
     */
    resetStats(): void {
        this.stats = {
            totalQueries: 0,
            gpuQueries: 0,
            cpuQueries: 0,
            objectsCulled: 0,
            visibleObjects: 0,
            avgQueryTimeMs: 0,
            mipChainBuildTimeMs: 0,
            isWebGPU: this.isWebGPU,
            pyramidSize: this.config.pyramidSize,
            activeMipLevels: this.config.mipLevels
        };
    }

    /**
     * Clear visibility cache
     */
    clearCache(): void {
        this.visibilityCache.clear();
    }

    /**
     * Dispose all resources
     */
    dispose(): void {
        // WebGPU cleanup
        if (this.hizPyramid) {
            this.hizPyramid.destroy();
            this.hizPyramid = null;
        }
        this.hizPyramidViews = [];
        this.hizStorageViews = [];
        this.mipBuildPipeline = null;
        this.batchQueryPipeline = null;
        this.gpuDevice = null;
        this.isWebGPU = false;

        // WebGL2 cleanup
        if (this.depthRenderTarget) {
            this.depthRenderTarget.dispose();
            this.depthRenderTarget = null;
        }
        if (this.depthMaterial) {
            this.depthMaterial.dispose();
            this.depthMaterial = null;
        }
        this.hizPyramidLevels = [];

        // Clear collections
        this.occluders.clear();
        this.occludees.clear();
        this.boundingSpheres.clear();
        this.visibilityCache.clear();
    }
}

// ============================================================================
// SINGLETON & FACTORY
// ============================================================================

let _hizInstance: FikoHiZOcclusionCulling | null = null;

/**
 * Get or create HiZ occlusion culling instance
 */
export function getHiZOcclusionCulling(
    camera?: THREE.Camera,
    renderer?: THREE.WebGLRenderer,
    config?: Partial<HiZConfig>
): FikoHiZOcclusionCulling | null {
    if (!_hizInstance && camera && renderer) {
        _hizInstance = new FikoHiZOcclusionCulling(camera, renderer, config);
    }
    return _hizInstance;
}

/**
 * Dispose singleton instance
 */
export function disposeHiZOcclusionCulling(): void {
    if (_hizInstance) {
        _hizInstance.dispose();
        _hizInstance = null;
    }
}
